import React, { useState, useRef, useCallback, useEffect } from "react";
import { usePdf } from "@mikecousins/react-pdf";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { apiClient } from "../../api/apiClient";
import screenfull from "screenfull";
import Controls from "./Controls";

const PdfViewer = ({ fileName, onClose }) => {
  // NOTE: previous code had a desktop flag that was *always false* – removing for clarity.
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null); // replaces previewRef for clarity

  const [page, setPage] = useState(1);
  const [currentSigUrl, setCurrentSigUrl] = useState(fileName);
  const [scale, setScale] = useState(0.5);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState(false);

  // (Removed unused dimension tracking – no longer influencing layout.)

  // Refresh signed URL (if needed) every 30s – stabilized effect (previously recreated every 5s)
  useEffect(() => {
    let isCancelled = false;

    const fetchSignature = async () => {
      try {
        if (
          fileName &&
          fileName.includes("/attachments/") &&
          fileName.toLowerCase().endsWith(".pdf")
        ) {
          const url = fileName.replace(
            "/attachments/",
            "/attachments/_signature/",
          );
          const signatureResponse = await apiClient().get(url, {});
          if (isCancelled) return;
          const { signature } = signatureResponse.data || {};
          if (signature) {
            setCurrentSigUrl(`${fileName}?sig=${signature}`);
          } else {
            setCurrentSigUrl(fileName); // fallback
          }
        } else {
          setCurrentSigUrl(fileName);
        }
      } catch (e) {
        // silent fail – keep last known URL
      }
    };

    fetchSignature();
    const interval = setInterval(fetchSignature, 30000); // 30s instead of 5s – less churn
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [fileName]);

  const onDocumentLoadFail = useCallback(() => {
    setError(true);
  }, []);

  const onToggleFullscreen = useCallback(() => {
    if (!screenfull.isEnabled) return;
    if (!wrapperRef.current) return;

    if (fullscreen) {
      screenfull.exit();
    } else {
      screenfull.request(wrapperRef.current);
    }
  }, [fullscreen]);

  // Keep local fullscreen state in sync with screenfull events
  useEffect(() => {
    if (!screenfull.isEnabled) return;
    const handler = () => {
      setFullscreen(screenfull.isFullscreen);
      // reset scale to 1 when entering fullscreen for readability
      if (screenfull.isFullscreen) {
        setScale(1);
      }
    };
    screenfull.on("change", handler);
    return () => {
      screenfull.off("change", handler);
    };
  }, []);

  const { pdfDocument } = usePdf({
    file: fileName, // keeping original logic (signed URL only used for direct download)
    page,
    canvasRef,
    scale,
    onDocumentLoadFail: onDocumentLoadFail,
  });

  const loading = !error && !pdfDocument; // show spinner until document loads

  const downloadFile = useCallback(() => {
    window.open(currentSigUrl, "_blank");
  }, [currentSigUrl]);

  const onSetPage = useCallback((value) => {
    setPage(value);
  }, []);

  const onScale = useCallback((value) => {
    setScale(value);
  }, []);

  const DocumentLoadFailedMessage = () => (
    <div style={{ padding: 3 }}>
      <Typography variant="body1" gutterBottom>
        Es ist ein Fehler aufgetreten
      </Typography>
      <Button variant="contained" color="primary" onClick={onReload}>
        Neu Laden
      </Button>
    </div>
  );

  const onReload = useCallback(() => window.location.reload(), []);

  return (
    <Box
      ref={wrapperRef}
      id="wrapper"
      sx={(theme) => ({
        bgcolor: theme.palette.grey[300],
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      })}
    >
      {error && <DocumentLoadFailedMessage />}
      {loading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!loading && pdfDocument && pdfDocument.numPages > 0 && (
        <Controls
          numPages={pdfDocument.numPages}
          downloadFile={downloadFile}
          onToggleFullScreen={onToggleFullscreen}
          page={page}
          scale={scale}
          onScale={onScale}
          onSetPage={onSetPage}
          onClose={onClose}
          fullscreen={fullscreen}
          currentSigUrl={currentSigUrl}
        >
          <Box
            sx={{
              overflow: "auto",
              my: 3,
              maxHeight: "100%",
            }}
          >
            <Box>
              <canvas ref={canvasRef} />
            </Box>
          </Box>
        </Controls>
      )}
    </Box>
  );
};
export default PdfViewer;
